import { CloseIcon } from "@stubhub/broadway-web/icons/functional";
import type { ISiteMessage } from "src/http/types";
import { Content } from "src/shared/components/content";
import { EmailOptInContentView } from "../email-opt-in-banner/EmailOptInContentView";
import {
  popupCloseButton,
  popupContainer,
  popupContentArea,
  popupHeader,
  popupSubtitle,
  popupTitle,
} from "./email-opt-in-popup.styles";
import { useEmailOptInPopupVisibility } from "./useEmailOptInPopupVisibility";

interface EmailOptInPopupProps {
  readonly privacyPolicyMessage?: ISiteMessage | undefined;
  /**
   * Set by the parent when the email opt-in slot scrolls into view — the arm-agnostic
   * trigger for exp 103701. The popup only renders for popup-arm users with a qualifying gate.
   */
  readonly isTriggered: boolean;
  /**
   * When true the UserEventToast is potentially visible in the same bottom-right corner.
   * The popup is suppressed to prevent the two fixed elements from overlapping.
   */
  readonly hasUserEventToast?: boolean;
}

/**
 * Non-blocking email opt-in popup. Renders as a fixed-position card at the bottom
 * of the viewport — does not trap focus or block page interaction. Gated on the
 * cookie consent banner being dismissed, and dismisses itself permanently once
 * the user closes it or subscribes.
 */
export const EmailOptInPopup = (props: EmailOptInPopupProps) => {
  const { privacyPolicyMessage, isTriggered, hasUserEventToast = false } = props;
  const { isVisible, dismiss, markSubscribed } = useEmailOptInPopupVisibility(isTriggered, hasUserEventToast);

  if (!isVisible) {
    return null;
  }

  return (
    <div role="dialog" aria-modal="false" aria-labelledby="email-opt-in-popup-title" className={popupContainer}>
      <div className={popupHeader}>
        <p id="email-opt-in-popup-title" className={popupTitle}>
          <Content id="cid$#4220766#" />
        </p>
        <button type="button" onClick={dismiss} aria-label="Close" className={popupCloseButton}>
          <CloseIcon />
        </button>
      </div>
      <p className={popupSubtitle}>
        <Content id="cid$#4220767#" />
      </p>
      <div className={popupContentArea}>
        <EmailOptInContentView privacyPolicyMessage={privacyPolicyMessage} onEmailSignUp={markSubscribed} />
      </div>
    </div>
  );
};
